import {Work} from 'notebook/workers/core/work';

/**
 * A wrapper around a Web Worker that gives a nice Promise based interface without the caller having to worry
 * that the work is being done by a Web Worker.
 *
 * @template T - The type of the payload to send to the worker.
 * @template U - The type of the result returned by the worker.
 *
 * @param {Work<T, U>} work - The actual Web Worker function from notebook/workers/work
 *
 * @returns {(payload: T) => Promise<U>} - A function that takes a payload of type T,
 *                                         sends it to the worker, and returns a promise
 *                                         that resolves with the worker's response of type U.
 *
 * @example
 * const echoWork = () => work((s: String) => s);
 * const delegateEcho = delegate(echoWork);
 *
 * delegateEcho("Hello World!").then((result) => {
 *   console.log(result);
 * });
 */
export default function delegate<T, U>(work: Work<T, U>): (payload: T) => Promise<U> {
  return (payload) =>
    new Promise((resolve) => {
      const worker = work();
      worker.onmessage = ({data}) => {
        worker.terminate();
        resolve(data);
      };
      try {
        worker.postMessage(payload);
      } catch (err) {
        worker.terminate();
        throw err;
      }
    });
}
